import { Direction, TilePos, CatState } from '../types';
import { Cat } from '../entities/Cat';

/**
 * Each cat personality computes a target tile differently:
 * - Whiskers: targets player's current tile (direct chase)
 * - Shadow: targets 4 tiles ahead of player's facing direction (ambush)
 * - Mittens: vector from Whiskers to 2 ahead of player, doubled (tricky)
 * - Chonk: chases when far, scatters when close (lazy)
 */
export function getTargetTile(
  cat: Cat,
  catIndex: number,
  playerTile: TilePos,
  playerDir: Direction,
  whiskersTile: TilePos,
  state: CatState
): TilePos {
  if (state === CatState.Patrol) {
    return cat.scatterTarget;
  }

  if (state === CatState.Scared) {
    return cat.scatterTarget;
  }

  switch (catIndex) {
    case 0: return whiskersBehavior(playerTile);
    case 1: return shadowBehavior(playerTile, playerDir);
    case 2: return mittensBehavior(playerTile, playerDir, whiskersTile);
    case 3: return chonkBehavior(playerTile, cat.tilePos, cat.scatterTarget);
    default: return playerTile;
  }
}

function whiskersBehavior(playerTile: TilePos): TilePos {
  return { ...playerTile };
}

function shadowBehavior(playerTile: TilePos, playerDir: Direction): TilePos {
  const ahead = 4;
  const { dc, dr } = dirToDelta(playerDir);
  return {
    col: playerTile.col + dc * ahead,
    row: playerTile.row + dr * ahead,
  };
}

function mittensBehavior(playerTile: TilePos, playerDir: Direction, whiskersTile: TilePos): TilePos {
  const { dc, dr } = dirToDelta(playerDir);
  const aheadCol = playerTile.col + dc * 2;
  const aheadRow = playerTile.row + dr * 2;
  return {
    col: aheadCol + (aheadCol - whiskersTile.col),
    row: aheadRow + (aheadRow - whiskersTile.row),
  };
}

function chonkBehavior(playerTile: TilePos, ownTile: TilePos, scatterTarget: TilePos): TilePos {
  const dist = Math.sqrt(
    (ownTile.col - playerTile.col) ** 2 +
    (ownTile.row - playerTile.row) ** 2
  );
  return dist > 8 ? { ...playerTile } : scatterTarget;
}

function dirToDelta(dir: Direction): { dc: number; dr: number } {
  switch (dir) {
    case Direction.Up: return { dc: 0, dr: -1 };
    case Direction.Down: return { dc: 0, dr: 1 };
    case Direction.Left: return { dc: -1, dr: 0 };
    case Direction.Right: return { dc: 1, dr: 0 };
    default: return { dc: 0, dr: 0 };
  }
}
